export const TELEMETRY_EVENT_NAMES = [
    "app_open",
    "login",
    "logout",
    "project_list",
    "project_create",
    "project_open",
    "project_save",
    "project_delete",
    "project_import",
    "collaboration_share_create",
    "collaboration_share_copy",
    "collaboration_push",
    "collaboration_pull",
    "collaboration_force_push",
    "collaboration_conflict",
    "collaboration_unavailable",
    "panel_toggle",
    "extension_add",
    "project_run_start",
    "project_run_stop",
    "client_error",
] as const;

export type TelemetryEventName = typeof TELEMETRY_EVENT_NAMES[number];
export type TelemetryErrorCategory = "handled_operation" | "react_boundary" | "server" | "unhandled_rejection" | "unknown" | "window";
type TelemetryPropertyKey = "accountType" | "mode" | "reason" | "result" | "source" | "state" | "trigger";
type TelemetryProperties = Partial<Record<TelemetryPropertyKey, boolean | string>>;

export interface TelemetryEventInput {
    errorCategory?: TelemetryErrorCategory;
    kind: "action" | "error";
    message?: string;
    name: string;
    properties?: Record<string, boolean | string>;
    stack?: string;
}

export interface TelemetryEvent extends TelemetryEventInput {
    appRelease: string;
    clientSessionId: string;
    occurredAt: string;
    route: string;
}

interface TelemetryClientOptions {
    appRelease: string;
    clientSessionId: string;
    route: () => string;
    send: (events: TelemetryEvent[]) => Promise<void>;
}

const EVENT_NAMES = new Set<string>(TELEMETRY_EVENT_NAMES);
const PROPERTY_KEYS = new Set<string>([
    "accountType",
    "mode",
    "reason",
    "result",
    "source",
    "state",
    "trigger",
]);
const sensitiveValues = new Set<string>();

const replaceSensitiveValues = (value: string): string => {
    let redacted = value;
    for (const sensitive of sensitiveValues) {
        if (sensitive.length >= 3)
            redacted = redacted.replace(
                new RegExp(sensitive.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&"), "giu"),
                () => "[REDACTED_NAME]",
            );
    }
    return redacted;
};

export const registerTelemetrySensitiveValue = (value: string | null | undefined): void => {
    const normalized = value?.trim();
    if (normalized && normalized.length >= 3) sensitiveValues.add(normalized);
};

const truncateUtf8 = (value: string, limit: number): string => {
    const encoder = new TextEncoder();
    if (encoder.encode(value).byteLength <= limit) return value;
    let low = 0;
    let high = value.length;
    while (low < high) {
        const midpoint = Math.ceil((low + high) / 2);
        if (encoder.encode(value.slice(0, midpoint)).byteLength <= limit) low = midpoint;
        else high = midpoint - 1;
    }
    let result = value.slice(0, low);
    while (result && encoder.encode(result).byteLength > limit) result = result.slice(0, -1);
    return result;
};

export const redactTelemetryText = (value: string, limit: number): string => {
    const redacted = replaceSensitiveValues(value)
        .replace(/\b[a-z]{3}-[a-z]{3}\b/giu, "[REDACTED_CODE]")
        .replace(/\b[0-9]{3}-[0-9]{3}\b/gu, "[REDACTED_CODE]")
        .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/giu, "[REDACTED_UUID]")
        .replace(/\b[A-Za-z0-9_-]{32,}\b/gu, "[REDACTED_TOKEN]")
        .replace(/https?:\/\/[^\s]+/giu, "[REDACTED_URL]")
        .replace(/\b(?:postgres(?:ql)?|sqlstate|password|relation|constraint|column|table)\b[^\n]*/giu, "[REDACTED_DATABASE]");
    return truncateUtf8(redacted, limit);
};

export const normalizeTelemetryRoute = (pathname: string): string => pathname
    .replace(/\/l\/[A-Za-z0-9_-]{22}(?:\/|$)/gu, "/l/:code")
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?:\/|$)/giu, "/:id")
    .slice(0, 256);

const sanitizedProperties = (properties: TelemetryEventInput["properties"]): TelemetryProperties => {
    const result: TelemetryProperties = {};
    for (const [key, value] of Object.entries(properties ?? {})) {
        if (!PROPERTY_KEYS.has(key)) throw new Error("UNKNOWN_TELEMETRY_PROPERTY");
        if (typeof value === "string") result[key as TelemetryPropertyKey] = value.slice(0, 64);
        else result[key as TelemetryPropertyKey] = value;
    }
    return result;
};

const sanitizedEvent = (
    input: TelemetryEventInput,
    options: TelemetryClientOptions,
): TelemetryEvent => {
    if (!EVENT_NAMES.has(input.name)) throw new Error("UNKNOWN_TELEMETRY_EVENT");
    return {
        appRelease: options.appRelease,
        clientSessionId: options.clientSessionId,
        occurredAt: new Date().toISOString(),
        route: options.route(),
        kind: input.kind,
        name: input.name,
        properties: sanitizedProperties(input.properties),
        ...(input.errorCategory ? { errorCategory: input.errorCategory } : {}),
        ...(input.message ? { message: redactTelemetryText(input.message, 2048) } : {}),
        ...(input.stack ? { stack: redactTelemetryText(input.stack, 12 * 1024) } : {}),
    };
};

export const createTelemetryClient = (options: TelemetryClientOptions) => {
    const queue: TelemetryEvent[] = [];
    const errorKeys = new Set<string>();
    let timer: ReturnType<typeof setInterval> | undefined;
    let pagehideListener: (() => void) | undefined;
    let activeFlush: Promise<void> | undefined;
    let activeBatchSize = 0;

    const nextBatch = (): TelemetryEvent[] => {
        const batch: TelemetryEvent[] = [];
        while (queue.length && batch.length < 20) {
            const event = queue[0];
            const candidate = [...batch, event];
            if (new TextEncoder().encode(JSON.stringify({ events: candidate })).byteLength > 64 * 1024)
                break;
            batch.push(event);
            queue.shift();
        }
        return batch;
    };
    const flush = (): Promise<void> => {
        if (activeFlush) return activeFlush;
        activeFlush = (async () => {
            while (queue.length) {
                const batch = nextBatch();
                if (!batch.length) {
                    queue.shift();
                    continue;
                }
                activeBatchSize = batch.length;
                try {
                    await options.send(batch);
                } catch {
                    // Telemetry is best effort and must never change application behavior.
                } finally {
                    activeBatchSize = 0;
                }
            }
        })().finally(() => {
            activeFlush = undefined;
        });
        return activeFlush;
    };
    const record = (input: TelemetryEventInput): void => {
        if (queue.length + activeBatchSize === 100) {
            if (!queue.length) return;
            queue.shift();
        }
        queue.push(sanitizedEvent(input, options));
        if (queue.length >= 20) void flush();
    };
    const recordError = (error: unknown, category: TelemetryErrorCategory): void => {
        try {
            const message = error instanceof Error ? error.message : String(error);
            const stack = error instanceof Error ? error.stack : undefined;
            const key = `${category}\0${redactTelemetryText(message, 2048)}\0${redactTelemetryText(stack ?? "", 12 * 1024)}`;
            if (errorKeys.has(key)) return;
            errorKeys.add(key);
            record({
                errorCategory: category,
                kind: "error",
                message,
                name: "client_error",
                properties: {},
                stack,
            });
        } catch {
            try {
                const key = `${category}\0[UNREADABLE_ERROR]`;
                if (errorKeys.has(key)) return;
                errorKeys.add(key);
                record({
                    errorCategory: category,
                    kind: "error",
                    message: "[UNREADABLE_ERROR]",
                    name: "client_error",
                    properties: {},
                });
            } catch {
                // Malformed diagnostic data must never affect the application.
            }
        }
    };
    const flushOnPagehide = (): void => {
        while (queue.length) {
            const batch = nextBatch();
            if (!batch.length) {
                queue.shift();
                continue;
            }
            void options.send(batch).catch(() => undefined);
        }
    };
    const start = (): void => {
        if (timer) return;
        timer = setInterval(() => void flush(), 10_000);
        if (typeof window !== "undefined") {
            pagehideListener = flushOnPagehide;
            window.addEventListener("pagehide", pagehideListener);
        }
    };
    const stop = (): void => {
        if (timer) clearInterval(timer);
        timer = undefined;
        if (pagehideListener && typeof window !== "undefined")
            window.removeEventListener("pagehide", pagehideListener);
        pagehideListener = undefined;
    };
    return {
        flush,
        peek: () => [...queue],
        pendingCount: () => queue.length + activeBatchSize,
        record,
        recordError,
        start,
        stop,
    };
};

const randomTabId = (): string => {
    const bytes = new Uint8Array(16);
    try {
        crypto.getRandomValues(bytes);
    } catch {
        for (let index = 0; index < bytes.length; index += 1)
            bytes[index] = Math.floor(Math.random() * 256);
    }
    let binary = "";
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/gu, "");
};

const TAB_ID_KEY = "scratch-cg-telemetry-tab";
export const getTelemetryTabId = (
    storage?: Pick<Storage, "getItem" | "setItem">,
): string => {
    let activeStorage = storage;
    if (!activeStorage) {
        try {
            activeStorage = window.sessionStorage;
        } catch {
            return randomTabId();
        }
    }
    try {
        const stored = activeStorage.getItem(TAB_ID_KEY);
        if (stored && /^[A-Za-z0-9_-]{22}$/u.test(stored)) return stored;
    } catch {
        return randomTabId();
    }
    const created = randomTabId();
    try {
        activeStorage.setItem(TAB_ID_KEY, created);
    } catch {
        // A private browsing policy may make sessionStorage unavailable.
    }
    return created;
};

const sendEvents = async (events: TelemetryEvent[]): Promise<void> => {
    const response = await fetch("/api/telemetry/v1/events", {
        body: JSON.stringify({ events }),
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        method: "POST",
    });
    if (!response.ok) throw new Error("TELEMETRY_DELIVERY_FAILED");
};

let globalClient: ReturnType<typeof createTelemetryClient> | undefined;
const preInitializationEvents: (
    | { input: TelemetryEventInput; type: "event" }
    | { category: TelemetryErrorCategory; error: unknown; type: "error" }
)[] = [];
let initializationStarted = false;

const bufferBeforeInitialization = (
    item: typeof preInitializationEvents[number],
): void => {
    if (preInitializationEvents.length === 100) preInitializationEvents.shift();
    preInitializationEvents.push(item);
};

export const readTelemetryAppRelease = async (
    fetchImpl: typeof fetch = fetch,
    timeoutMs = 1_500,
): Promise<string> => {
    const controller = new AbortController();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
        return await Promise.race([
            (async () => {
                const response = await fetchImpl("/release.json", {
                    credentials: "same-origin",
                    signal: controller.signal,
                });
                if (!response.ok) return "development";
                const body = (await response.json()) as { release?: unknown };
                return typeof body.release === "string" && /^[A-Za-z0-9._-]{1,128}$/u.test(body.release)
                    ? body.release
                    : "development";
            })(),
            new Promise<string>((resolve) => {
                timeout = setTimeout(() => {
                    controller.abort();
                    resolve("development");
                }, timeoutMs);
            }),
        ]);
    } catch {
        return "development";
    } finally {
        if (timeout) clearTimeout(timeout);
    }
};

export const wrapTelemetryFetch = (
    fetchImpl: typeof fetch,
    currentOrigin: string,
    reportError: (error: unknown, category: TelemetryErrorCategory) => void,
): typeof fetch => async (...arguments_) => {
        const response = await fetchImpl(...arguments_);
        try {
            const requested = arguments_[0] instanceof Request ? arguments_[0].url : String(arguments_[0]);
            const url = new URL(requested, currentOrigin);
            if (
                response.status >= 500 &&
                url.origin === currentOrigin &&
                url.pathname.startsWith("/api/") &&
                url.pathname !== "/api/telemetry/v1/events"
            ) {
                reportError(
                    new Error(`HTTP_${response.status} ${normalizeTelemetryRoute(url.pathname)}`),
                    "server",
                );
            }
        } catch {
            // Inspecting a response for telemetry must not affect the response itself.
        }
        return response;
    };

interface TelemetryEventTarget {
    addEventListener: (type: string, listener: EventListener) => void;
    removeEventListener: (type: string, listener: EventListener) => void;
}

export const installTelemetryGlobalErrorCapture = (
    target: TelemetryEventTarget,
    reportError: (error: unknown, category: TelemetryErrorCategory) => void,
): (() => void) => {
    const onError: EventListener = (event) => {
        const errorEvent = event as ErrorEvent;
        reportError(errorEvent.error ?? errorEvent.message, "window");
    };
    const onRejection: EventListener = (event) => {
        reportError((event as PromiseRejectionEvent).reason, "unhandled_rejection");
    };
    target.addEventListener("error", onError);
    target.addEventListener("unhandledrejection", onRejection);
    return () => {
        target.removeEventListener("error", onError);
        target.removeEventListener("unhandledrejection", onRejection);
    };
};

export const initializeTelemetry = async (): Promise<void> => {
    if (globalClient || initializationStarted) return;
    initializationStarted = true;
    try {
        window.fetch = wrapTelemetryFetch(
            window.fetch.bind(window),
            window.location.origin,
            trackTelemetryError,
        );
    } catch {
        // The telemetry client can still operate without automatic 5xx capture.
    }
    try {
        installTelemetryGlobalErrorCapture(window, trackTelemetryError);
    } catch {
        // The telemetry client can still record explicit operation failures.
    }
    const appRelease = await readTelemetryAppRelease();
    const client = createTelemetryClient({
        appRelease,
        clientSessionId: getTelemetryTabId(),
        route: () => normalizeTelemetryRoute(window.location.pathname),
        send: sendEvents,
    });
    // eslint-disable-next-line require-atomic-updates -- initializationStarted serializes initialization across the await.
    globalClient = client;
    client.start();
    client.record({ kind: "action", name: "app_open", properties: {} });
    for (const item of preInitializationEvents.splice(0)) {
        if (item.type === "event") client.record(item.input);
        else client.recordError(item.error, item.category);
    }
};

export const trackTelemetryAction = (name: TelemetryEventName, properties: TelemetryProperties = {}): void => {
    try {
        const input: TelemetryEventInput = { kind: "action", name, properties };
        if (globalClient) globalClient.record(input);
        else bufferBeforeInitialization({ input, type: "event" });
    } catch {
        // Callers must never fail because telemetry rejected an event.
    }
};

export const trackTelemetryError = (error: unknown, category: TelemetryErrorCategory): void => {
    try {
        if (globalClient) globalClient.recordError(error, category);
        else bufferBeforeInitialization({ category, error, type: "error" });
    } catch {
        // Error reporting is fail-safe and never reports its own failure.
    }
};
